import {
  StructureSetEntry,
  useAutomationIntegrationApiV1GetStructuresQuery,
  useLazyWebApiV1GetRtStructsQuery,
  useLazyWebApiV1OrderQuery,
} from "@providers/hop-ord-server/api";
import { useEffect } from "react";
import { useAutomationIntegrationApiV1RequestStructuresMutation } from "@providers/hop-ord-server/api";
import { useAutomationIntegrationApiV1GetImportedDataHasRefreshedSinceQuery } from "@providers/hop-ord-server/api";
import { useCallback } from "react";
import { useAutomationIntegrationApiV1RefreshImportedDataMutation } from "@providers/hop-ord-server/api";
import { useState } from "react";
import { StructureStatus } from "@enums";

export type RefreshStatus =
  | "idle"
  | "requesting refresh"
  | "awaiting refresh"
  | "refetching order"
  | "requesting structures"
  | "awaiting structures";

interface StructuresRetrieved {
  retrieved: boolean;
  structures: StructureSetEntry[];
}

const useRefreshImportedData = (
  orderId: number,
  selectedRtStructId: number,
  selectedTemplate: string,
) => {
  const [previouslyRefreshedAt, setPreviouslyRefreshedAt] = useState<
    string | null
  >(null);
  const [refreshStatus, setRefreshStatus] = useState<RefreshStatus>("idle");
  const [_structuresRequested, setStructuresRequested] = useState(false);
  const [structuresRetrieved, setStructuresRetrieved] =
    useState<StructuresRetrieved>({ retrieved: false, structures: [] });
  const [refreshImportedData] =
    useAutomationIntegrationApiV1RefreshImportedDataMutation();

  const [getOrder] = useLazyWebApiV1OrderQuery();
  const [sendavailableStructuresResponseRequest] =
    useAutomationIntegrationApiV1RequestStructuresMutation();

  const [getRTStructs] = useLazyWebApiV1GetRtStructsQuery();
  const refreshingImportedData = refreshStatus !== "idle";

  const handleRefresh = useCallback(async () => {
    setRefreshStatus("requesting refresh");
    const { lastRefreshedAt } = await refreshImportedData({ orderId }).unwrap();
    setPreviouslyRefreshedAt(lastRefreshedAt ?? null);
    setRefreshStatus("awaiting refresh");
  }, [orderId, refreshImportedData]);

  const shouldPollRefreshedSince = refreshStatus === "awaiting refresh";
  const { data: importedDataRefreshedSince } =
    useAutomationIntegrationApiV1GetImportedDataHasRefreshedSinceQuery(
      {
        orderId,
        sinceTimestamp: previouslyRefreshedAt ?? undefined,
      },
      {
        pollingInterval: shouldPollRefreshedSince ? 2000 : 0,
        skip: !shouldPollRefreshedSince,
      },
    );

  useEffect(() => {
    if (!importedDataRefreshedSince) {
      return;
    }
    const { hasRefreshedSince, lastRefreshedAt } = importedDataRefreshedSince;

    if (hasRefreshedSince && lastRefreshedAt) {
      // This has refreshed
      setRefreshStatus("refetching order");
    }
  }, [importedDataRefreshedSince, selectedTemplate]);

  useEffect(() => {
    if (refreshStatus !== "refetching order") {
      return;
    }
    getOrder({ orderId })
      .unwrap()
      .then((order) => {
        // Check if order can have structures requested from TPS
        if (
          order.rtstructStatus !== StructureStatus.COMPLETE ||
          selectedTemplate === ""
        ) {
          return Promise.reject();
        }
        setRefreshStatus("requesting structures");
        return sendavailableStructuresResponseRequest({
          orderId,
          rtStructId: selectedRtStructId,
        }).unwrap();
      })
      .then(() => {
        setRefreshStatus("awaiting structures");
        setStructuresRetrieved({ retrieved: false, structures: [] });
        setStructuresRequested(true);
      })
      .catch(() => {
        setRefreshStatus("idle");
      })
      .finally(() => {
        // Retrieve the RT Structures for the order, this will update the RTK cache for RtStructs;
        getRTStructs({ orderId });
      });
  }, [refreshStatus, selectedTemplate, getRTStructs]);

  const shouldPollStructures =
    selectedTemplate !== "" &&
    !structuresRetrieved.retrieved &&
    refreshStatus === "awaiting structures";

  const { currentData: availableStructuresResponse } =
    useAutomationIntegrationApiV1GetStructuresQuery(
      {
        orderId,
        sinceTimestamp: previouslyRefreshedAt ?? undefined,
        rtStructId: selectedRtStructId,
      },
      {
        pollingInterval: shouldPollStructures ? 2000 : 0,
        skip: !shouldPollStructures,
        refetchOnMountOrArgChange: true,
      },
    );

  useEffect(() => {
    if (refreshStatus === "awaiting structures" && !shouldPollStructures) {
      setRefreshStatus("idle");
    } else if (
      refreshStatus === "awaiting structures" &&
      availableStructuresResponse?.structures?.length
    ) {
      setStructuresRetrieved({
        retrieved: true,
        structures: availableStructuresResponse.structures,
      });
      setRefreshStatus("idle");
    }
  }, [
    availableStructuresResponse,
    refreshStatus,
    shouldPollStructures,
    refreshingImportedData,
  ]);

  return {
    handleRefresh,
    lastRefreshedAt:
      importedDataRefreshedSince?.lastRefreshedAt ?? previouslyRefreshedAt,
    refreshStatus,
    setRefreshStatus,
    structuresRetrieved,
  };
};

export default useRefreshImportedData;
